import { defineEntity } from './_helpers.js';

/**
 * Lifecycle-only entity catalog.
 *
 * Every entity here automatically gets `created`, `updated`, `deleted` events
 * emitted by `base.controller.js` from its `resourceName`. Listed here so the
 * catalog audit script (`audit-event-naming.mjs`) accepts those names without
 * the OCP requirement of a per-entity dedicated file.
 *
 * Entities with **custom** events live in their own `<entity>.events.js`.
 * Adding a new entity = appending a string here OR creating a dedicated file
 * (both are valid; dedicated file wins on collision).
 */
const LIFECYCLE_ONLY_ENTITIES = [
    // Organization & system
    'company', 'unit', 'team', 'vendor', 'process', 'matrixConfiguration',
    'subjectPersonGroup', 'mailTemplate', 'notificationTemplate', 'systemLog',
    // Asset (extra events live in asset.events.js)
    'assetCategory', 'assetGroup', 'assetGroupCriticality',
    'assetDisposal', 'assetLifecycleEvent',
    // Risk taxonomy & supporting
    'threat', 'riskAnalysis', 'riskTreatment', 'riskConsequence',
    'riskMitigationDefinition', 'riskReports',
    // BCM supporting
    'bcmInitialResponseLibrary', 'bcmRpo', 'bcmSettings', 'recoveryResources',
    'biaRpoCalculation', 'biaRpoRecommendation',
    'crisisRecoveryActivity', 'lessonLearned',
    'exerciseParticipant', 'scenarioTimeline',
    'exerciseRecoveryActivity', 'exerciseEvaluation',
    'teamAssignment', 'teamDutyProfile', 'dutyDefinition',
    'bcpLocation',
    // ISMS / compliance
    'communication', 'communicationPlan', 'meeting', 'context', 'interestedParty',
    'serviceCatalogItem', 'problem', 'vulnerability', 'scopeDocument',
    'objective', 'competence', 'kpi', 'weakness', 'strategicInitiative',
    'complianceObligation', 'complianceFramework', 'regulatoryClause', 'regulatorySource',
    // Audit & control
    'scfControlEvaluation', 'gap', 'assessmentQuestion',
    'auditPlan', 'auditAssignment', 'auditFinding',
    // GDPR
    'explicitConsent', 'consentRecord', 'legalReason',
    'collectionMethod',
    'explicitConsentReason', 'explicitConsentMethod', 'kvkkCompany',
    // Forms / reports
    'reportArchive', 'formTemplate', 'surveyDefinition',
    'formSubmission', 'pendingApproval', 'formTask', 'governanceReportDefinition',
    'reportConfidentialityClass', 'dpiaTemplate',
    // Documents/classification
    'documentType', 'classification', 'assignmentTemplate',
    'workflowTemplateDraft', 'workflowActionCreateEntityRecord',
    'documentRevision',
    // BIGDES (KVKK)
    'bigdesCompensatoryControl', 'bigdesRoadMapWorkPackage',
    'personalDataCategory', 'securityMeasure',
    'recipientGroup', 'processingPurpose',
    'dataDisposalMethod', 'dataSource', 'retentionPeriod',
    'transferType', 'internationalTransfer', 'privacyPrinciple', 'personalDataInventory',
    // Tickets supporting
    'ticketSla', 'ticketCategory', 'ticketTemplate', 'ticketComment', 'ticketRecurrence',
    // Incidents supporting
    'incidentComment',
    'incidentTemplate',
    // Tailorability / admin
    'tailorability', 'webhookSubscription', 'integrationConnector',
    'integrationProviderDefinition', 'integrationEndpoint', 'integrationInvocation',
    'communicationProvider',
    'pageApprovalConfig', 'pendingChange',
    // Misc
    'action', 'training', 'userTraining',
    'notification', 'changeRequest', 'dataSubjectRequest', 'dataBreach',
    'serviceCatalogItem',
    // Application tracking & vendor assessment (Wave-D coverage gap)
    'applicationMethod', 'applicationRequestInformation', 'applicationTracking',
    'vendorAssessment', 'ticketSLA',
    // Tailorability primitives — emit `<head>.<sub>.<verb>` shape via base
    'biaImpactConfiguration', 'tenantDirectory',
    'custom', 'entity',
];

/**
 * Build catalog entries for all lifecycle-only entities.
 */
export const LIFECYCLE_ONLY_CATALOG = LIFECYCLE_ONLY_ENTITIES.map((entity) => defineEntity(entity));
